// How the wheel shells divide their horizontal space: the drawer's rail + stage + detail, and the
// dashboard's stage + detail since R11 moved its panel list into a toolbar dropdown.
//
// The order is: the wheel takes as much HEIGHT as the stage allows, and only what is genuinely
// left over goes to the detail column. A square wheel on a wide monitor is height-bound, so
// without the second step the centre column keeps a pocket of dead space either side of the
// circle; without the first, the panel takes width the wheel should have grown into.
//
// R12 adds a THIRD rule that inverts that order. A research surface or a chat is not a report
// beside a wheel — it is the thing being worked in — so `paneWidthFor` names the column's width
// from the viewport first and the wheel takes what is left. The two rules never both apply: the
// dashboard picks one per render from whether a pane is open.
//
// R14 puts ONE ceiling over both of the dashboard's states: whichever rule named the width, the
// right column never passes half the viewport. It is the LAST step of each, so which state the
// column is in cannot change the answer — and it REPLACES R12's 55% share, which passed half by
// construction. `capColumn` is that ceiling and both rules end on it.

export const MIN_WHEEL = 320;

export const DETAIL_MIN_WIDTH = 400;

// Sized to close the pocket on a 1878x918 monitor once the wheel has taken its height. Past this
// the report is a page, not a panel — and it answers the width with a two-column card grid and a
// capped prose measure, see PanelReport/panelReport.css.
export const DETAIL_MAX_WIDTH = 720;

// The gap between the stage and the column beside it, in the dashboard's arithmetic and in
// paneWidthFor's cap — one value, because the cap is only honest if it is the real gutter.
export const STAGE_GUTTER = 32;

// R14's ceiling, and the pane's viewport share now that the two are one number.
export const COLUMN_VIEWPORT_CAP = 0.5;

// Past this a chat is a wide measure rather than a working column, so the extra goes to the wheel.
export const PANE_MAX_WIDTH = 960;

// The one ceiling, applied last by both dashboard rules.
//
// ⚠️ DETAIL_MIN_WIDTH WINS THE CAP BELOW 800px, deliberately. From 768 down the column overlays
// the stage and takes no flow width from the wheel, so half the viewport is protecting nothing
// there; and across 768–799 the cap would trade a usable editor measure for at most 16px.
const capColumn = (preferred: number, viewportWidth: number): number =>
	Math.round(Math.max(DETAIL_MIN_WIDTH, Math.min(preferred, viewportWidth * COLUMN_VIEWPORT_CAP)));

// The wheel is square, so it is the smaller of the two axes. No maximum: "as much height as
// possible" is the requirement, and a bigger circle is a more legible one.
//
// ⚠️ NO MINIMUM EITHER, and that is deliberate. The drawer still carries a rail, and flooring at
// MIN_WHEEL pushes its three columns past a 1024px viewport (300 rail + 320 + 400 panel + 32
// gutter = 1052). The dashboard's two columns clear that unaided (320 + 400 + 32 = 752), so its
// floor is now free rather than load-bearing. The shells that can scroll or stack still own it.
export const wheelSizeFor = (centreWidth: number, availableHeight: number): number =>
	Math.round(Math.min(centreWidth, availableHeight));

// `centreWidth` is the real stage width — whatever the shell's other columns leave it — measured
// with the detail column at its MINIMUM, so widening the panel can never feed back and shrink the
// wheel that sized it.
//
// ⚠️ `viewportWidth` is optional because the DRAWER opts out of R14, not because a caller may
// forget it: its three columns are a different trade from the dashboard's two, and R14 is scoped
// to the builder. WheelModal is the only omitter and a test pins that.
export const detailWidthFor = (centreWidth: number, wheelSize: number, viewportWidth?: number): number => {
	const slack = Math.max(0, Math.round(centreWidth - wheelSize));
	const pocket = Math.min(DETAIL_MAX_WIDTH, DETAIL_MIN_WIDTH + slack);
	return viewportWidth === undefined ? pocket : capColumn(pocket, viewportWidth);
};

// The pane state's width. It reads the VIEWPORT and nothing else — not the wheel, not the stage —
// which is what keeps it out of the measure/resize loop the two-step rule above exists to avoid.
//
// ⚠️ The cap is MIN_WHEEL expressed as a width, and it has to be here: wheelSizeFor deliberately
// has no minimum, so nothing downstream would stop the column squeezing the circle out. Below
// 752px the floor wins and the wheel does go under MIN_WHEEL — that is the compact branch, where
// the column overlays the stage rather than taking flow width from it.
export const paneWidthFor = (viewportWidth: number): number =>
	capColumn(Math.min(PANE_MAX_WIDTH, viewportWidth - MIN_WHEEL - STAGE_GUTTER), viewportWidth);
